import React from 'react';
import { Button } from './ui/button';
import { ArrowRight, Plane } from 'lucide-react';

const Hero = () => {
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Blueprint Background with Grid */}
      <div className="absolute inset-0 z-0" style={{
        backgroundColor: '#0f1729',
        backgroundImage: `
          linear-gradient(rgba(59, 130, 246, 0.1) 1px, transparent 1px),
          linear-gradient(90deg, rgba(59, 130, 246, 0.1) 1px, transparent 1px)
        `,
        backgroundSize: '50px 50px'
      }}>
        {/* Fixed-Wing UAS Silhouette SVG */}
        <svg 
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 opacity-20"
          width="800" 
          height="400" 
          viewBox="0 0 800 400"
          style={{ filter: 'drop-shadow(0 0 20px rgba(59, 130, 246, 0.3))' }}
        >
          {/* Fuselage */}
          <path d="M 250 200 L 600 200 L 650 190 L 680 190 L 690 195 L 690 205 L 680 210 L 650 210 L 600 200 Z" 
                fill="rgba(59, 130, 246, 0.4)" stroke="rgba(59, 130, 246, 0.6)" strokeWidth="2"/>
          
          {/* Main Wings */}
          <path d="M 400 200 L 400 80 L 450 90 L 450 200 Z" 
                fill="rgba(59, 130, 246, 0.4)" stroke="rgba(59, 130, 246, 0.6)" strokeWidth="2"/>
          <path d="M 400 200 L 400 320 L 450 310 L 450 200 Z" 
                fill="rgba(59, 130, 246, 0.4)" stroke="rgba(59, 130, 246, 0.6)" strokeWidth="2"/>
          
          {/* Tail Fin */}
          <path d="M 650 190 L 650 140 L 680 150 L 680 190 Z" 
                fill="rgba(59, 130, 246, 0.4)" stroke="rgba(59, 130, 246, 0.6)" strokeWidth="2"/>
          
          {/* Horizontal Stabilizer */}
          <path d="M 650 200 L 650 170 L 700 175 L 700 200 Z" 
                fill="rgba(59, 130, 246, 0.4)" stroke="rgba(59, 130, 246, 0.6)" strokeWidth="2"/>
          <path d="M 650 200 L 650 230 L 700 225 L 700 200 Z" 
                fill="rgba(59, 130, 246, 0.4)" stroke="rgba(59, 130, 246, 0.6)" strokeWidth="2"/>
          
          {/* Generic Technical Callouts */}
          <circle cx="425" cy="135" r="3" fill="rgba(59, 130, 246, 0.8)"/>
          <line x1="425" y1="135" x2="480" y2="100" stroke="rgba(59, 130, 246, 0.5)" strokeWidth="1" strokeDasharray="2,2"/>
          <text x="485" y="105" fill="rgba(59, 130, 246, 0.6)" fontSize="11" fontFamily="monospace">WING ASSEMBLY</text>
          
          <circle cx="350" cy="200" r="3" fill="rgba(59, 130, 246, 0.8)"/>
          <line x1="350" y1="200" x2="300" y2="240" stroke="rgba(59, 130, 246, 0.5)" strokeWidth="1" strokeDasharray="2,2"/>
          <text x="200" y="245" fill="rgba(59, 130, 246, 0.6)" fontSize="11" fontFamily="monospace">AVIONICS BAY</text>
          
          <circle cx="665" cy="165" r="3" fill="rgba(59, 130, 246, 0.8)"/>
          <line x1="665" y1="165" x2="720" y2="130" stroke="rgba(59, 130, 246, 0.5)" strokeWidth="1" strokeDasharray="2,2"/>
          <text x="725" y="135" fill="rgba(59, 130, 246, 0.6)" fontSize="11" fontFamily="monospace">TAIL SECTION</text>
        </svg>
        
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/80 via-slate-800/70 to-blue-900/75"></div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          <div className="mb-6 inline-flex items-center space-x-2 bg-blue-500/20 backdrop-blur-sm px-4 py-2 rounded-full border border-blue-400/30">
            <Plane className="w-4 h-4 text-blue-300" />
            <span className="text-blue-200 text-sm font-medium">Unmanned Aviation Engineering</span>
          </div>
          
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
            Engineering Excellence for
            <span className="block mt-2 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-cyan-300">
              Unmanned Aviation
            </span>
          </h1>
          
          <p className="text-xl md:text-2xl text-slate-300 mb-10 max-w-3xl mx-auto leading-relaxed">
            Engineering support for unmanned aviation programs, tailored for rapid iteration and practical execution.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button 
              onClick={() => scrollToSection('contact')}
              size="lg"
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg font-semibold transition-all duration-200 hover:scale-105 shadow-lg hover:shadow-xl group"
            >
              Contact Us for Consultation
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform duration-200" />
            </Button>
            <Button 
              onClick={() => scrollToSection('services')}
              size="lg"
              variant="outline"
              className="border-2 border-slate-300 text-white hover:bg-white/10 px-8 py-6 text-lg font-semibold transition-all duration-200 backdrop-blur-sm"
            >
              Learn More About Services
            </Button>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10 animate-bounce">
        <div className="w-6 h-10 border-2 border-slate-400 rounded-full flex items-start justify-center p-2">
          <div className="w-1.5 h-2 bg-slate-400 rounded-full"></div>
        </div>
      </div>
    </section>
  );
};

export default Hero;


